// CategoryOverview.jsx
import React from 'react';

import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { getSupportedLanguages } from '../selector/getRemoteConfigComputedData.js';
import { prepareListPageMeta } from '../resource/prepareListPageMeta.js';
import UniversalComponent from '../component/UniversalComponent.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  TRENDING_CATEGORY_FEED,
  TRENDING_CATEGORY_FETCH_LIMIT,
} from '../RemoteConfigKeys.js';
import fetchFeeds from '../action/fetchFeeds.js';
import shouldSkipSSRFetch from '../resource/shouldSkipSSRFetch.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('CategoryOverview loader()');
  const errorLog = legacySSRError.extend('CategoryOverview loader()');
  log('start', { context, params, request });

  const isServer = typeof window === 'undefined';
  // The redux store is provided via loader context (server: requestContext, client: getContext).
  // Loaders dispatch directly into it — the same store that
  // is rendered / serialized to __PRELOADED_STATE__
  // the data is present before the first render (SSR and navigation).
  const store = context?.store;
  if (!store) {
    if (isServer) {
      errorLog('context.store is missing on server', { params });
      throw new Error(
        'CategoryOverviewRoute loader requires context.store (server: query requestContext)'
      );
    }
    log('context.store missing on client, skipping loader');
    return { params, shouldSkipHydration: true };
  }

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const skipSSRFetch =
      isServer &&
      shouldSkipSSRFetch({
        requestUrl: new URL(request.url),
        state: store.getState(),
      });

    if (skipSSRFetch) {
      log('skip SSR fetch due to buster query');
      // shouldSkipHydration: incomplete result — keep it out of
      // __staticRouterHydrationData so the client loader re-runs on the
      // initial load instead of hydrating a data-less page.
      return { params, shouldSkipHydration: true };
    }

    const configState = store.getState();
    const trendingCategoryFeed = getRemoteConfigData(
      configState,
      TRENDING_CATEGORY_FEED
    );

    const fetchLimit = getRemoteConfigData(
      configState,
      TRENDING_CATEGORY_FETCH_LIMIT
    );

    if (trendingCategoryFeed) {
      await store.dispatch(
        fetchFeeds({
          type: trendingCategoryFeed,
          limit: fetchLimit,
          page: 1,
          httpProxyHeaders,
        })
      );
    }

    const state = store.getState();
    const storeData = state.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getSupportedLanguages(state);
    const canonicalPath = '/category-overview';
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareListPageMeta({
      state,
      titleKey: 'seo_overview_category_title',
      descriptionKey: 'seo_overview_category_description',
      keywordsKey: 'seo_overview_category_keywords',
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      jsonLdType: 'WebSite',
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    return {
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    // shouldSkipHydration: a failed server fetch must not be hydrated, so the
    // client loader re-runs on the initial load and retries the fetch.
    return { params, shouldSkipHydration: true };
  }
};

const CategoryOverviewRoute = () => {
  return (
    <UniversalComponent
      filename="Container_MessageCategoryMore"
      importer={
        /* #__LOADABLE__ */ () => import('../container/MessageCategoryMore.js')
      }
    />
  );
};

export default CategoryOverviewRoute;
